import React, { useState, useEffect } from 'react';

// Shared widget layout catalog + renderer, used by BOTH the dashboard widget
// builder preview and the live embed, so the preview can never drift from
// what customers actually see. Callers pass a `Card` render-prop that wraps
// their own card component, since the builder and embed use different ones.

export const WIDGET_LAYOUTS = [
  { value: 'masonry', label: 'Masonry Grid' },
  { value: 'grid', label: 'Standard Grid' },
  { value: 'carousel', label: 'Swipe Carousel' },
  { value: 'marquee', label: 'Auto-Scroll Marquee' },
  { value: 'spotlight', label: 'Rotating Spotlight' },
  { value: 'list', label: 'Compact List' }
];

// One card at a time, auto-rotating. Great for sidebars and hero strips.
const SpotlightRotator = ({ items, Card }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  const item = items[idx % items.length];
  return (
    <div className="w-full max-w-xl mx-auto">
      <div key={item.id}>{Card(item)}</div>
      {items.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {items.map((_, i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full transition-all duration-300"
              style={{ backgroundColor: i === idx ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.2)' }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const renderWidgetLayout = ({ layout, items, Card, isMobile = false }) => {
  if (!items || items.length === 0) return null;

  if (layout === 'grid') {
    return (
      <div className={`grid gap-4 sm:gap-6 w-full ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
        {items.map(t => <div key={t.id}>{Card(t)}</div>)}
      </div>
    );
  }

  if (layout === 'carousel') {
    return (
      <div className="flex items-start gap-4 sm:gap-6 overflow-x-auto pb-6 scrollbar-hide snap-x w-full">
        {items.map(t => (
          <div key={t.id} className={`snap-center flex-shrink-0 ${isMobile ? 'w-[260px]' : 'w-[300px] sm:w-[360px]'}`}>
            {Card(t)}
          </div>
        ))}
      </div>
    );
  }

  if (layout === 'marquee') {
    // Duplicate the list so translateX(-50%) loops seamlessly. Pause on hover.
    const dur = Math.max(20, items.length * 6);
    return (
      <div className="overflow-hidden w-full">
        <div className="mot-wmq flex items-start gap-4 sm:gap-6 w-max" style={{ animation: `mot-wmq-scroll ${dur}s linear infinite` }}>
          {[...items, ...items].map((t, i) => (
            <div key={`${t.id}-${i}`} className={`flex-shrink-0 ${isMobile ? 'w-[260px]' : 'w-[300px] sm:w-[340px]'}`}>
              {Card(t)}
            </div>
          ))}
        </div>
        <style>{`@keyframes mot-wmq-scroll{from{transform:translateX(0)}to{transform:translateX(-50%)}} .mot-wmq:hover{animation-play-state:paused}`}</style>
      </div>
    );
  }

  if (layout === 'spotlight') {
    return <SpotlightRotator items={items} Card={Card} />;
  }

  if (layout === 'list') {
    return (
      <div className="flex flex-col gap-4 w-full max-w-2xl mx-auto">
        {items.map(t => <div key={t.id}>{Card(t)}</div>)}
      </div>
    );
  }

  // masonry (default) — CSS columns; wrapper carries break-inside-avoid + margin
  return (
    <div className={`gap-4 sm:gap-6 w-full ${isMobile ? 'columns-1' : 'columns-1 sm:columns-2 lg:columns-3'}`}>
      {items.map(t => <div key={t.id} className="break-inside-avoid mb-4 sm:mb-6">{Card(t)}</div>)}
    </div>
  );
};
